import { createStubImplementation } from '@ardoq/stub-implementation';
import {
  APIComponentType,
  APIModelAttributes,
  APIReferenceType,
  APIWorkspaceAttributes,
  ArdoqId,
  LabeledValue,
  WorkspaceIntegration,
} from '@ardoq/api-types';
import { returnZero } from '@ardoq/common-helpers';

export type IsExternallyManaged = (workspaceId: ArdoqId) => boolean;

export type IsFlexible = (workspaceId: ArdoqId) => boolean;

export type GetComponentTypes = (workspaceId: ArdoqId) => APIComponentType[];

export type GetCssClassNames = (workspaceId: ArdoqId) => string;

export type GetAttribute = <Key extends keyof APIWorkspaceAttributes>(
  workspaceId: ArdoqId,
  key: Key
) => APIWorkspaceAttributes[Key] | null;

export type GetWorkspaceCount = () => number;

export type GetModelData = (workspaceId: ArdoqId) => APIModelAttributes | null;

export type GetTypeById = (
  workspaceId: ArdoqId,
  typeId: string
) => APIComponentType | null;

export type GetWorkspaceName = (workspaceId: ArdoqId) => string | null;

export type GetWorkspaceIntegrations = (
  workspaceId: ArdoqId
) => WorkspaceIntegration[];

export type HasWriteAccess = (workspaceId: ArdoqId) => boolean;

export type GetWorkspaceIdByModelId = (modelId: ArdoqId) => ArdoqId | null;

export type GetLoadedWorkspaceOptions = () => LabeledValue<ArdoqId>[];

export type GetReferenceTypeById = (
  workspaceId: string,
  referenceTypeId: string | number
) => APIReferenceType | null;

export type GetLoadedWorkspaceIds = () => string[];

export type GetWorkspaceModelId = (workspaceId: ArdoqId) => ArdoqId | null;

export type GetWorkspaceData = (
  workspaceId: ArdoqId
) => APIWorkspaceAttributes | null;

export type GetNameToComponentTypeId = (
  workspaceIds: ArdoqId[],
  componentTypeName: string
) => Record<ArdoqId, string | number | undefined>;

export type GetNameToReferenceTypeId = (
  workspaceIds: ArdoqId[],
  referenceTypeName: string
) => Record<ArdoqId, string | number | undefined>;

export type GetReferenceTypes = (workspaceId: ArdoqId) => APIReferenceType[];

export type GetWorkspace = (id: ArdoqId) => APIWorkspaceAttributes | null;

export type IsWorkspace = (workspaceId: ArdoqId) => boolean;

type WorkspaceExists = (workspaceId?: ArdoqId | null) => boolean;

export type WorkspaceInterfaceImplementation = {
  get: GetWorkspace;
  isExternallyManaged: IsExternallyManaged;
  getComponentTypes: GetComponentTypes;
  isFlexibleWorkspace: IsFlexible;
  isWorkspace: IsWorkspace;
  workspaceExists: WorkspaceExists;
  getCssClassNames: GetCssClassNames;
  getAttribute: GetAttribute;
  getModelData: GetModelData;
  getTypeById: GetTypeById;
  getWorkspaceName: GetWorkspaceName;
  getWorkspaceIntegrations: GetWorkspaceIntegrations;
  hasWriteAccess: HasWriteAccess;
  getWorkspaceIdByModelId: GetWorkspaceIdByModelId;
  getLoadedWorkspaceOptions: GetLoadedWorkspaceOptions;
  getReferenceTypeById: GetReferenceTypeById;
  getLoadedWorkspaceIds: GetLoadedWorkspaceIds;
  getWorkspaceModelId: GetWorkspaceModelId;
  getWorkspaceData: GetWorkspaceData;
  getNameToComponentTypeId: GetNameToComponentTypeId;
  getNameToReferenceTypeId: GetNameToReferenceTypeId;
  getWorkspaceCount: GetWorkspaceCount;
  getReferenceTypes: GetReferenceTypes;
};

const defaultImplementation: WorkspaceInterfaceImplementation = {
  get: () => null,
  isExternallyManaged: () => false,
  getComponentTypes: () => [],
  isFlexibleWorkspace: () => false,
  isWorkspace: () => false,
  workspaceExists: () => false,
  getCssClassNames: () => '',
  getAttribute: () => null,
  getModelData: () => null,
  getTypeById: () => null,
  getWorkspaceName: () => null,
  getWorkspaceIntegrations: () => [],
  hasWriteAccess: () => false,
  getWorkspaceIdByModelId: () => null,
  getLoadedWorkspaceOptions: () => [],
  getReferenceTypeById: () => null,
  getLoadedWorkspaceIds: () => [],
  getWorkspaceModelId: () => null,
  getWorkspaceData: () => null,
  getNameToComponentTypeId: () => ({}),
  getNameToReferenceTypeId: () => ({}),
  getWorkspaceCount: returnZero,
  getReferenceTypes: () => [],
};

const {
  get,
  isExternallyManaged,
  getComponentTypes,
  isFlexibleWorkspace,
  isWorkspace,
  workspaceExists,
  getCssClassNames,
  getAttribute,
  getModelData,
  getTypeById,
  getWorkspaceName,
  getWorkspaceIntegrations,
  hasWriteAccess,
  getWorkspaceIdByModelId,
  getLoadedWorkspaceOptions,
  getReferenceTypeById,
  getLoadedWorkspaceIds,
  getWorkspaceModelId,
  getWorkspaceData,
  getNameToComponentTypeId,
  getNameToReferenceTypeId,
  registerImplementation,
  getWorkspaceCount,
  getReferenceTypes,
} = createStubImplementation(
  '@ardoq/workspace-interface',
  defaultImplementation
);

/**
 * `workspaceInterface` is a Backbone-era singleton: every member resolves the
 * workspace, and often walks on to its model, from global mutable state at call
 * time.
 *
 * The app is stream-based now. Resolve workspaces from `workspaces$`
 * (`streams/workspaces/workspaces$.ts`, exported as `persistentWorkspaces$`) at a
 * permitted boundary -- a `*$.ts` file, a file inside a `*$/` folder, or a
 * `*routines.ts` -- and thread the resolved data downstream as plain parameters.
 *
 * When a member walks `workspace.componentModel`, join `workspaceAndModel$`
 * (`streams/workspaceAndModel$.ts`) rather than `workspaces$` and `models$`
 * separately: it filters out workspaces whose model is not loaded yet, so the
 * `scopeDataOperations` helpers (`@ardoq/scope-data`) cannot observe the transient
 * mismatch.
 *
 * The pure counterparts live in `modelOperations` (`@ardoq/core`) for anything
 * that reads a model's type tables -- `getComponentTypes`,
 * `getComponentTypesWithParents`, `getReferenceTypes`, `getRefTypeById`,
 * `getRefTypeName`, `getStyle`, `getLineBeginning`, `getLineEnding`,
 * `getValidTypeByParent` -- and in `workspaceUtils` (`@ardoq/scope-data`) for
 * the workspace's own attributes.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const workspaceInterface = {
  defaultImplementation,
  registerImplementation,
  /**
   * @deprecated Read `workspaces$.byId[id]`.
   */
  get,
  /**
   * @deprecated Read the attribute off `workspaces$.byId[id]`.
   */
  getAttribute,
  /**
   * @deprecated Join `workspaceAndModel$` and read `scopeDataOperations.toWorkspaceWithTypes(...).componentTypes` (`@ardoq/scope-data`), or call `modelOperations.getComponentTypes(model)` (`@ardoq/core`); `componentTypesByWorkspaceId$` also exposes this.
   */
  getComponentTypes,
  /**
   * @deprecated Call `scopeDataOperations.getWorkspaceAndModel` (`@ardoq/scope-data`) on `workspaceAndModel$`, or `workspaceUtils.getWorkspaceModel`; `workspaceModelDataByWorkspaceId$` also exposes this.
   */
  getModelData,
  /**
   * @deprecated Call `modelOperations.getComponentTypes(model).componentTypesById[typeId]` (`@ardoq/core`) with the model from `workspaceAndModel$`.
   */
  getTypeById,
  /**
   * @deprecated Join `workspaceAndModel$` and build the classes from the resolved workspace and model in a pure helper.
   */
  getCssClassNames,
  /**
   * @deprecated Read `workspaces$.byId[id]?.name`.
   */
  getWorkspaceName,
  /**
   * @deprecated Call `workspaceUtils.getWorkspaceIntegrations(scopeData, workspaceId)` (`@ardoq/scope-data`).
   */
  getWorkspaceIntegrations,
  /**
   * @deprecated Join `currentUserPermissionContext$` and call the pure `workspaceAccessControlInterface.canEditWorkspace(permissionContext, workspaceId, activeScenario)`, or `permissionsOperations.hasEditAccessToResource` (`@ardoq/access-control`).
   */
  hasWriteAccess,
  /**
   * @deprecated Read `workspaces$.models` and find on `componentModel`, or call `enhancedScopeDataOperations.getWorkspaceIdByComponentModelId` (`@ardoq/scope-data`).
   */
  getWorkspaceIdByModelId,
  /**
   * @deprecated Call `workspaceUtils.isExternallyManaged(scopeData, workspaceId)` (`@ardoq/scope-data`).
   */
  isExternallyManaged,
  /**
   * @deprecated Call `workspaceUtils.isFlexibleWorkspace` (`@ardoq/scope-data`), or read `workspaces$.byId[id]`.
   */
  isFlexibleWorkspace,
  /**
   * @deprecated Join `context$` and `workspaces$` and shape the options in a pure helper.
   */
  getLoadedWorkspaceOptions,
  /**
   * @deprecated Call `modelOperations.getRefTypeById(referenceTypes, id)` (`@ardoq/core`), or `workspaceUtils.getReferenceTypeById` (`@ardoq/scope-data`).
   */
  getReferenceTypeById,
  /**
   * @deprecated Join `context$` and read `workspaceIds`; `workspaceUtils.getAllWorkspaceIds` (`@ardoq/scope-data`) covers the scope-data case.
   */
  getLoadedWorkspaceIds,
  /**
   * @deprecated Read `workspaces$.byId[id]?.componentModel`.
   */
  getWorkspaceModelId,
  /**
   * @deprecated Call `scopeDataOperations.getWorkspaceAndModel` (`@ardoq/scope-data`) on `workspaceAndModel$` -- it returns `{ workspace, model }` with both guaranteed present.
   */
  getWorkspaceData,
  /**
   * @deprecated Join `workspaceAndModel$` and read `scopeDataOperations.toWorkspaceWithTypes(...).referenceTypes` (`@ardoq/scope-data`), or call `modelOperations.getReferenceTypes(model)` (`@ardoq/core`); `referenceTypesByWorkspaceId$` also exposes this.
   */
  getReferenceTypes,
  /**
   * @deprecated Read `workspaces$.byId[id]` for presence, or call `workspaceUtils.isWorkspace` (`@ardoq/scope-data`).
   */
  isWorkspace,
  /**
   * @deprecated Build the lookup from `modelOperations.getComponentTypes(model).componentTypesById` (`@ardoq/core`).
   */
  getNameToComponentTypeId,
  /**
   * @deprecated Build the lookup from `modelOperations.getReferenceTypes(model)` (`@ardoq/core`).
   */
  getNameToReferenceTypeId,
  /**
   * @deprecated Read `workspaces$.byId[id]` for presence, or call `workspaceUtils.isWorkspace` (`@ardoq/scope-data`).
   */
  workspaceExists,
  /**
   * @deprecated Read `workspaces$.models.length`.
   */
  getWorkspaceCount,
};
